import toast from 'react-hot-toast';
import { getErrorMessage } from './errors';
import { toastCheckIcon, toastWarningIcon } from './toastIcons';
import { MILLIS_IN_HOUR, MILLIS_IN_SECOND } from './dateUtil';
import { ButtonTypes, CommonButton } from '../components/common/Forms/Button';
import { FaCopy } from 'react-icons/fa';
import i18n from '../i18n';
import copy from 'copy-to-clipboard';

const TOAST_DURATION = 5 * MILLIS_IN_SECOND;
const ERROR_TOAST_DURATION = 15 * MILLIS_IN_SECOND;

type ErrorToastDismissMode = 'auto' | 'manual';
const DEFAULT_ERROR_TOAST_DISMISS_MODE: ErrorToastDismissMode = 'auto';
const MANUAL_DISMISS_ERROR_TOAST_DURATION = 24 * MILLIS_IN_HOUR; // Simulate persistent toast for manual dismiss

let errorToastDismissMode: ErrorToastDismissMode =
  DEFAULT_ERROR_TOAST_DISMISS_MODE;

export const setErrorToastDismissMode = (mode: ErrorToastDismissMode) => {
  errorToastDismissMode = mode;
};

export const resetErrorToastDismissMode = () => {
  errorToastDismissMode = DEFAULT_ERROR_TOAST_DISMISS_MODE;
};

export const errorToastPromise = async <T,>(
  promise: PromiseLike<T>,
  onError?: () => void,
) => {
  try {
    return await promise;
  } catch (e) {
    errorToast(e);
    onError?.();
  }
};

export const errorToast = (
  err?: any,
  options: { dismissMode?: ErrorToastDismissMode } = {},
) => {
  errorToastMessage(getErrorMessage(err), options);
};

const errorToastMessage = (
  errorMessage: string,
  {
    dismissMode = errorToastDismissMode,
  }: { dismissMode?: ErrorToastDismissMode } = {},
) => {
  toast.error(
    ({ id }) =>
      createErrorToastMessageElement({
        toastId: id,
        message: errorMessage,
        buttonVariant: 'errorToast',
        withCloseButton: true,
      }),
    {
      duration:
        dismissMode === 'manual'
          ? MANUAL_DISMISS_ERROR_TOAST_DURATION
          : ERROR_TOAST_DURATION,
      className: 'app-toast',
      icon: toastWarningIcon(),
      style: {
        background: 'var(--rednegative)',
        color: 'var(--white)',
      },
      role: 'alert',
    },
  );
};

const createErrorToastMessageElement = ({
  toastId,
  message,
  buttonVariant,
  withCloseButton,
}: {
  toastId: string;
  message: string;
  buttonVariant: ButtonTypes;
  withCloseButton: boolean;
}) => {
  return (
    <div className="app-toast-error-message">
      <div>{message}</div>
      <div className="buttons">
        {withCloseButton && (
          <CommonButton
            variant={buttonVariant}
            onClick={() => toast.dismiss(toastId)}
          >
            {i18n.t('close')}
          </CommonButton>
        )}
        <CommonButton
          size="auto"
          iconOnly
          variant={buttonVariant}
          title={i18n.t('copy-error-to-clipboard')}
          onClick={(e) => {
            e.currentTarget.blur();
            if (copy(message)) {
              successToast(i18n.t('copied-error-to-clipboard'));
            }
          }}
        >
          <FaCopy />
        </CommonButton>
      </div>
    </div>
  );
};

export const successToast = (message: string) => {
  toast.success(message, {
    duration: TOAST_DURATION,
    className: 'app-toast',
    icon: toastCheckIcon(),
    style: {
      background: 'var(--greenprimary)',
      color: 'var(--white)',
    },
  });
};
